import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  ArmTemplate,
  CreateArmTemplate,
  UpdateArmTemplate,
} from '@estimator/shared';

export function useArmTemplates() {
  return useQuery({
    queryKey: ['arm-templates'],
    queryFn: async () => {
      const { data } = await apiClient.get<ArmTemplate[]>('/banquette-arm-templates');
      return data;
    },
  });
}

export function useArmTemplate(id: string | null) {
  return useQuery({
    queryKey: ['arm-templates', id],
    queryFn: async () => {
      const { data } = await apiClient.get<ArmTemplate>(`/banquette-arm-templates/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateArmTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateArmTemplate) => {
      const { data } = await apiClient.post<ArmTemplate>('/banquette-arm-templates', body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['arm-templates'] });
    },
  });
}

export function useUpdateArmTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateArmTemplate & { id: string }) => {
      const { data } = await apiClient.patch<ArmTemplate>(`/banquette-arm-templates/${id}`, body);
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['arm-templates'] });
      queryClient.invalidateQueries({ queryKey: ['arm-templates', vars.id] });
    },
  });
}

export function useDeleteArmTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/banquette-arm-templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['arm-templates'] });
    },
  });
}
